<template>
    <div class="row">
        <div class="col-sm-8 pb-3">
            <div class="row">
                <div class="col-sm-12">
                    <label>
                        Name <span class="color-6">*</span>
                    </label>

                    <div class="d-flex mb-2">
                        <input type="text" class="full" :class="{'border-color-6': $v.reservation.customer.name.$invalid && !$v.reservation.customer.name.required}" v-model="reservation.customer.name">
                    </div>
                </div>

                <div class="col-sm-6">
                    <label>
                        Country <span class="color-6">*</span>
                    </label>

                    <country-picker :topCodes="topCountries" :selectedCountryValue="selectedCountry" :disabled="false" :allowEmpty="false" @input="updateCountry($event, 'country')" :customClass="'mb-2'" :error="!selectedCountry" ></country-picker>
                </div>

                <div class="col-sm-6">
                    <label>Phone Number</label>

                    <div class="row">
                        <div class="col-4 pr-2">
                            <input type="text" class="fs-bigger bg-lighter w-100" placeholder="+351" v-model="reservation.customer.dial"  @input="onDialInput">
                        </div>

                        <div class="col-8 pl-0">
                            <input type="text" class="fs-bigger bg-lighter w-100" v-model="reservation.customer.phone">
                        </div>
                    </div>
                </div>

                <div class="col-sm-12">
                    <label>
                        Email
                        <span v-if="$v.reservation.customer.email.$invalid" class="color-6">
                            <span v-if="!$v.reservation.customer.email.email">Invalid</span>
                        </span>
                    </label>

                    <input type="email" class="full mb-2" v-model="reservation.customer.email">
                </div>

                <div class="col-sm-8">
                    <label>Hotel</label>

                    <input type="text" class="full" v-model="reservation.customer.accomodation">
                </div>

                <div class="col-sm-4">
                    <label>Room</label>

                    <input type="text" class="full" v-model="reservation.customer.room">
                </div>

                <div class="col-sm-12 d-flex align-items-center mt-3">
                    <checkbox class="mr-2" v-model="reservation.customer.sendEmailCopy"></checkbox>
                    
                    <label>Send a confirmation email to the customer</label>
                </div>
            </div>
        </div>

        <div class="col-sm-4 pb-3 flex-column">
            <div class="row h-100">
                <!-- Internal note -->
                <div v-if="canSeeInternalNote" class="col-sm-12 d-flex flex-column mb-3">
                    <label>
                        Internal notes:<span class="fs-smaller normal ml-1">(internal use only)</span>
                    </label>
                    <textarea
                        :disabled="!canEditInternalNote"
                        v-model="reservation.internalNote"
                        class="flex-grow-1 p-1 fs-bigger bg-lighter darker bold"
                        rows="1">
                    </textarea>
                </div>
                <!-- Public note -->
                <div class="col-sm-12 d-flex flex-column">
                    <label>Notes:<span class="fs-smaller normal ml-1">(agents can see these)</span></label>
                    <textarea
                        v-model="reservation.note"
                        class="flex-grow-1 p-1 fs-bigger bg-lighter darker bold"
                        rows="1">
                    </textarea>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { required, email, minLength } from 'vuelidate/lib/validators'

    import extendedVSelect from '@/extends/vue-select'
    import CountryPicker from '@/tools/CountryPicker'
    import debounce from 'lodash/debounce'

    export default {
        data() {
            return {
                selectedCountry: null,
                onDialInput: null,
                dialError: false,
                topCountries: ['PT', 'GB', 'US', 'ES', 'FR']
            }
        },
        props: {
            reservation: {
                type: Object
            },
            readOnly: {
                type: Boolean,
                default: false
            }
        },
        validations: {
            reservation:{
                customer: {
                    name: {
                        required
                    },
                    country: {
                        required
                    },
                    email: {
                        email
                    }
                }
            }
        },
        created() {
            this.setDefinedCountry();

            // 1000ms debounce to not interrupt typing
            this.onDialInput = debounce(() => {
                this.updateCountry('dial');
            }, 1000);
        },
        computed: {
            ...mapGetters([
                'getCountries',
                'getNamePrefixList',
                'getCanReadInternalNote',
                'getCanEditInternalNote'
            ]),
            canSeeInternalNote() {
                return this.getCanReadInternalNote;
            },
            canEditInternalNote() {
                return this.getCanEditInternalNote;
            }
        },
        methods: {
            setDefinedCountry() {
                // If there is a country defined in the reservation, select it
                if (this.reservation.customer && this.reservation.customer.country) {
                    this.selectedCountry = this.getCountries.find(storedCountry => 
                        storedCountry.id == this.reservation.customer.country
                    ) || null;
                }
            },
            updateCountry(event = null, type) {
                this.selectedCountry = event;
                
                if (type === 'country') {
                    this.applyCountryInfo(this.selectedCountry);
                    return;
                }

                // Dial/Code input
                const rawDial = this.reservation.customer.dial;
                const country = this.findCountryByDial(rawDial);

                if (country) {
                    this.selectedCountry = country;
                    this.applyCountryInfo(country);
                } else {
                    this.resetCountryInfo();
                }
            },
            findCountryByDial(dialInput) {
                let dial = (dialInput || '').replace(/\D/g, '');
                if (dial.startsWith('00')) dial = dial.slice(2);
                if (!dial) return null;

                // Filter countries
                const topCountries = this.getCountries.filter(c => this.topCountries.includes(c.code2));
                const allCountries = this.getCountries.filter(c => !this.topCountries.includes(c.code2));
                const finalCountries = [...topCountries, ...allCountries]

                // Finded country
                const country = finalCountries.find(n => n.dial === dial);
                
                if (country && country.length !== 0) {
                    return country;
                } else {
                    return null;
                }
            },
            applyCountryInfo(country) {
                this.reservation.customer.country = country.id;
                this.reservation.customer.dial = country.code;
                this.reservation.customer.countryName = country.name;
            },
            resetCountryInfo() {
                this.reservation.customer.country = null;
                this.reservation.customer.dial = null;
                this.reservation.customer.countryName = '';
                this.selectedCountry = null;
            }
        },
        components: {
            vSelect: extendedVSelect,
            CountryPicker
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../../assets/scss/_variables.scss";
    
    input, textarea {
        background-color: $lighter;
    }
</style>



// WEBPACK FOOTER //
// CustomerDetails.vue?01c83232